import { DEPLOYMENT_KEYS } from 'utils/constants'

/**
 * Normalize chain_deployment_info arm references to a list of IDs.
 * Supports study_schedule_ids array and legacy singular study_schedule_id.
 */
export const getChainDeploymentStudyScheduleIds = chainDeploymentInfo => {
  if (!chainDeploymentInfo) return []

  const studyScheduleIds = chainDeploymentInfo[DEPLOYMENT_KEYS.studyScheduleIds]
  if (Array.isArray(studyScheduleIds) && studyScheduleIds.length > 0) {
    return studyScheduleIds.filter(Boolean)
  }

  const studyScheduleId = chainDeploymentInfo[DEPLOYMENT_KEYS.studyScheduleId]
  if (studyScheduleId) {
    return [studyScheduleId]
  }

  return []
}

export const armsToCheckedObject = arms =>
  arms.reduce((acc, arm) => {
    acc[arm.key] = null
    return acc
  }, {})

export const armsToTags = arms =>
  arms.map(arm => ({
    id: arm.key,
    text: arm.text,
  }))

/**
 * Persist only arms that have at least one selected visit/cycle.
 * UI may still show a newly selected arm before visits are chosen.
 */
export const collectDeployVisits = chainDeploymentInfo => {
  if (!chainDeploymentInfo) return []

  const visitIds = chainDeploymentInfo.visit_ids
  const visitScheduleIds = chainDeploymentInfo.visit_schedule_ids

  if (Array.isArray(visitIds) && visitIds.length > 0) {
    return visitIds.map((visitId, index) => ({
      visit_id: visitId,
      visit_schedule_id: Array.isArray(visitScheduleIds)
        ? visitScheduleIds[index]
        : chainDeploymentInfo.visit_schedule_id,
      study_schedule_id: chainDeploymentInfo.study_schedule_id,
    }))
  }

  if (chainDeploymentInfo.visit_id) {
    return [
      {
        visit_id: chainDeploymentInfo.visit_id,
        visit_schedule_id: chainDeploymentInfo.visit_schedule_id,
        study_schedule_id: chainDeploymentInfo.study_schedule_id,
      },
    ]
  }

  return []
}

export const isVisitTriggeredSchedule = schedule =>
  !!(schedule?.chain_deployment_info && DEPLOYMENT_KEYS.chainDeployByVisit in schedule.chain_deployment_info)

const isDeployVisitInStudy = ({
  visit_id: visitId,
  visit_schedule_id: visitScheduleId,
  isVisitCycleSupported,
  studyVisitSchedule,
  visitTemplates,
}) => {
  if (!visitId || !visitScheduleId) return false

  if (isVisitCycleSupported) {
    if (!Array.isArray(studyVisitSchedule) || studyVisitSchedule.length === 0) return false
    return studyVisitSchedule.some(arm =>
      (arm.phases || []).some(phase =>
        (phase.cycles || []).some(
          cycle => cycle.id === visitScheduleId && (cycle.visits || []).some(visit => visit.id === visitId),
        ),
      ),
    )
  }

  if (!Array.isArray(visitTemplates) || visitTemplates.length === 0) return false
  return visitTemplates.some(
    template => template.id === visitScheduleId && (template.visits || []).some(visit => visit.id === visitId),
  )
}

/**
 * True when a "Triggered by Visit" schedule references visits that are missing from the study.
 */
export const scheduleHasMissingStudyVisits = (
  schedule,
  { visitTemplates = [], studyVisitSchedule = [], isVisitCycleSupported = false } = {},
) => {
  if (!isVisitTriggeredSchedule(schedule)) return false

  const deployVisits = collectDeployVisits(schedule.chain_deployment_info)
  if (!deployVisits.length) return true

  return deployVisits.some(
    visit =>
      !isDeployVisitInStudy({
        visit_id: visit.visit_id,
        visit_schedule_id: visit.visit_schedule_id,
        isVisitCycleSupported,
        studyVisitSchedule,
        visitTemplates,
      }),
  )
}

export const getInvalidVisitTriggeredScheduleIndexes = (schedules = [], options = {}) => {
  if (!Array.isArray(schedules) || schedules.length === 0) return []
  return schedules.reduce((acc, schedule, index) => {
    if (scheduleHasMissingStudyVisits(schedule, options)) acc.push(index)
    return acc
  }, [])
}

export const getPersistedStudyScheduleIds = (uiArmIds = [], visits = [], resolveArmId) => {
  const idsWithVisits = []
  visits.forEach(visit => {
    const armId = (resolveArmId && resolveArmId(visit)) || visit.study_schedule_id
    if (armId && !idsWithVisits.includes(armId)) {
      idsWithVisits.push(armId)
    }
  })

  if (uiArmIds.length > 0) {
    return uiArmIds.filter(id => idsWithVisits.includes(id))
  }

  return idsWithVisits
}
